import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { isEmdSendType } from '@Enums/sendType.enum';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import { useRequestFlowStore } from '@Stores/requestFlow.store';
import useDashboardContext from '@Use/useDashboardContext';

// Send-flow header names. A payment request (its flow includes the choose-payment-
// method step) is named by its payment purpose; a wire-only send has no method
// screen, so it keeps the generic "View Wire Instructions" label.
const CTC_TITLE = 'Send Cash to Close';
const EMD_TITLE = 'Send Earnest Money Deposit';
const WIRE_ONLY_TITLE = 'View Wire Instructions';
const CONFIRM_TITLE = 'Confirm Bank Details';
const COLLECT_TITLE = 'Submit Bank Details';
const IDENTITY_TITLE = 'Verify Identity';

// The wire-instructions routes are shared across the send, confirm, and collect
// task types, so the mobile nav-bar title derives from the requestType route param
// rather than the view that happens to render it.
export default function useTaskNavTitle() {
    const route = useRoute();
    const requestDetailsStore = useRequestDetailsStore();
    const requestFlowStore = useRequestFlowStore();
    const { isDashboardContext } = useDashboardContext();

    const taskNavTitle = computed(() => {
        // The task-name nav bar is a participant-portal affordance only; the
        // standalone transactional flow keeps its existing branded header.
        if (!isDashboardContext.value) {
            return '';
        }

        const requestType = route.params.requestType as string;

        switch (requestType) {
            case 'send': {
                // A wire-only send has no choose-payment-method step in its flow,
                // so it stays "View Wire Instructions". Payment requests are named
                // by purpose (Cash to Close vs Earnest Money Deposit).
                if (!requestFlowStore.hasRouteInFlow('payment-method-select')) {
                    return WIRE_ONLY_TITLE;
                }
                return isEmdSendType(requestDetailsStore.requestDetails?.sendType)
                    ? EMD_TITLE
                    : CTC_TITLE;
            }
            case 'confirm':
                return CONFIRM_TITLE;
            case 'collect':
                return COLLECT_TITLE;
            case 'identity':
                return IDENTITY_TITLE;
            default:
                return '';
        }
    });

    return { taskNavTitle };
}
